/**
 * canvasPayloadNormalizer
 * ────────────────────────────────────────────────────────────────────
 *
 * Canonicalizes a canvas workflow payload BEFORE it reaches the IR
 * walker (flatToIR) or the ELK layout pipeline. This is the load-time
 * counterpart to the backend's `CanvasAIService.validateAndRepairDAG`
 * Phase 4a/4b/4c — those phases protect NEW AI-generated payloads at
 * the API boundary; this module protects EXISTING saved workflows on
 * every load, so a one-time corrective pass heals legacy malformed
 * shapes without forcing the user to re-save every workflow.
 *
 * Architecture layering (production-grade defense in depth):
 *
 *   1. Source emission (primary defense)
 *      - CanvasAIService WORKFLOW_BUILDER_SYSTEM_PROMPT rules 9, 12-14
 *        explicitly forbid sentinel-outbounds, implicit fan-outs, and
 *        standalone parallel_joins.
 *      - CanvasAIService.validateAndRepairDAG strips violations at the
 *        API boundary before the response leaves the backend.
 *      - frontend/services/enforcementAutoInsertion.js Phase 5 skips
 *        flow-sentinel sources when injecting dpg_internode_scan
 *        nodes (G459).
 *
 *   2. Load-time normalization (this module) — heals existing saved
 *      workflows that pre-date the source fixes. Runs once per
 *      workflow load before flatToIR.
 *
 *   3. IR-walker accommodations (G455 absorbed-join logic, G458
 *      implicit-fan-out synthesis in flatToIR) — last-resort failsafes
 *      for anything that bypasses both layers above. The AI prompt
 *      and validator now forbid these patterns; the walker handles
 *      them gracefully if they slip through (e.g. hand-authored or
 *      legacy-imported workflows that this normalizer somehow misses).
 *
 * What this normalizer fixes:
 *
 *   • G459 — Strip outbound edges from terminate / end sentinel nodes.
 *     Sentinels MUST have zero outbounds per the workflow contract.
 *     Repro: Native Insurance Claim v37 had `n_terminate_ref →
 *     dpg_internode_scan → n_terminate_ref_2` — the DPG injector
 *     spliced past a malformed terminate-to-terminate edge.
 *     Healing: remove the outbound; the downstream node becomes an
 *     orphan that flatToIR will handle (or W133 will surface).
 *
 * Returns: { payload, repairs } where `payload` is the canonical
 * version (or the original input if no changes were needed) and
 * `repairs` is an array of repair records suitable for surfacing in
 * the UI repair banner.
 */

// Sentinel node types: per the workflow contract, these halt the flow
// and MUST NOT have outbound edges. terminate / end are the canonical
// sentinels; "complete" is the legacy Conductor end-state.
const SENTINEL_TYPES = new Set(['terminate', 'end', 'complete']);

/**
 * @param {object} payload  Canvas workflow payload with .nodes and .connections
 * @returns {{payload: object, repairs: Array<{kind: string, ...}>}}
 */
export function normalizeCanvasPayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return { payload, repairs: [] };
  }
  const nodes = Array.isArray(payload.nodes) ? payload.nodes : null;
  const connections = Array.isArray(payload.connections) ? payload.connections : null;
  if (!nodes || !connections) {
    return { payload, repairs: [] };
  }

  const repairs = [];
  const nodeMap = new Map();
  for (const n of nodes) {
    if (n && typeof n === 'object' && n.id) nodeMap.set(n.id, n);
  }

  // ── G459 — Strip outbound edges from sentinel-typed source nodes ──
  // Mirrors backend CanvasAIService.validateAndRepairDAG Phase 4a. A
  // terminate / end node MUST have zero outbound connections. If the
  // saved payload has one (legacy DPG injector misfire, hand-author
  // error, malformed import), we drop the edge here so the IR walker
  // sees a clean sentinel. The node downstream of the dropped edge
  // becomes an orphan; flatToIR's orphan-repair phase will re-anchor
  // it, or V2CanvasView's W133 will surface it for cleanup.
  const filteredConns = [];
  let strippedCount = 0;
  for (const c of connections) {
    if (!c || typeof c !== 'object' || !c.from) {
      filteredConns.push(c);
      continue;
    }
    const fromNode = nodeMap.get(c.from);
    const fromType = fromNode && typeof fromNode.type === 'string' ? fromNode.type : '';
    if (SENTINEL_TYPES.has(fromType)) {
      strippedCount += 1;
      repairs.push({
        kind: 'sentinel_outbound_stripped',
        from: c.from,
        fromType,
        to: c.to,
        connectionId: c.id,
      });
      continue;
    }
    filteredConns.push(c);
  }

  if (strippedCount === 0) {
    // No changes — return the original payload reference to keep
    // downstream identity checks happy.
    return { payload, repairs };
  }

  // eslint-disable-next-line no-console
  console.warn(
    `[canvasPayloadNormalizer] G459 — Stripped ${strippedCount} outbound edge(s) ` +
    `from terminate/end sentinel nodes. Sentinels MUST have zero outbounds. ` +
    `Affected: ${repairs.map((r) => `${r.from}(${r.fromType})→${r.to}`).join(', ')}`
  );

  return {
    payload: { ...payload, connections: filteredConns },
    repairs,
  };
}

/**
 * Convenience predicate — used by the IR walker and other downstream
 * consumers that want to know whether a given node id is a sentinel
 * (so they can short-circuit on it).
 */
export function isSentinelType(type) {
  return SENTINEL_TYPES.has(type);
}
